import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { ArrowRight, CheckCircle, User, Send, Users, Shield, Award } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { programs } from '../data/programs';
import { programDetails } from '../data/corporateDetails';

const CorporateDetailPage = () => {
  const { id } = useParams();
  const { theme } = useTheme();
  const program = programs.find(p => p.id === parseInt(id));
  const details = programDetails[id];
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!program || !details) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[var(--text-main)] mb-4">Program Not Found</h1>
          <Link to="/services/corporate" className="text-[var(--accent-purple)] hover:underline">Back to Corporate</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: 'e0929c9b-b3d7-48dd-9a03-8fc07927ab9d',
          ...formData,
          programId: program.id,
          programName: program.name,
        }),
      });

      const result = await response.json();
      setIsSubmitting(false);

      if (result.success) {
        toast.success('Inquiry submitted successfully!', { description: 'Our team will contact you within 24 hours.' });
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        toast.error('Failed to send inquiry. Please try again.');
      }
    } catch (error) {
      setIsSubmitting(false);
      toast.error('Failed to send inquiry. Please try again.');
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="corporate-detail-page">
      <Toaster position="top-right" theme={theme} toastOptions={{ style: { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-main)' } }} />

      {/* Hero */}
      <section className="py-10 md:py-16 relative overflow-hidden border-b border-[var(--border)]">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-main relative z-10">
          <Link to="/services/corporate" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] mb-6 font-mono text-sm">
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to Corporate Training
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <span className="px-3 py-1 bg-[var(--accent-purple)]/20 text-[var(--accent-purple)] text-xs font-mono uppercase mb-4 inline-block">{program.format}</span>
              <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)] mb-6">{program.name}</h1>
              <p className="text-[var(--text-muted)] text-lg leading-relaxed mb-6">{details.summary}</p>
              <div className="flex flex-wrap gap-2">
                {program.tags.map((tag, idx) => (
                  <span key={idx} className="px-3 py-1 bg-[var(--accent-purple)]/10 border border-[var(--accent-purple)]/30 text-sm text-[var(--accent-purple)] font-mono">{tag}</span>
                ))}
              </div>
            </div>
            <div className="bg-[var(--surface)] border border-[var(--border)] p-6">
              <div className="space-y-4 mb-6">
                <div><p className="text-xs text-[var(--text-muted)]">Duration</p><p className="font-mono text-2xl font-bold text-[var(--accent-purple)]">{program.duration}</p></div>
                <div><p className="text-xs text-[var(--text-muted)]">Location</p><p className="text-[var(--text-main)] font-mono">{program.format}</p></div>
              </div>
              <div className="border-t border-[var(--border)] pt-6">
                <p className="text-xs text-[var(--text-muted)]">Pricing</p>
                <p className="font-mono text-xl font-bold text-[var(--primary)]">Custom Quote</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trainer */}
      {/* <section className="py-16  border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-purple)] text-sm uppercase tracking-wider mb-6">./lead_trainer</p>
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 bg-[var(--surface-highlight)] border border-[var(--accent-purple)] flex items-center justify-center font-mono text-[var(--accent-purple)] text-2xl font-bold">
              {details.trainer.split(' ').map(n => n[0]).join('')}
            </div>
            <div>
              <h3 className="text-2xl font-bold text-[var(--text-main)] mb-2">{details.trainer}</h3>
              <p className="text-[var(--text-muted)] leading-relaxed">{details.trainerBio}</p>
            </div>
          </div>
        </div>
      </section> */}

      {/* Topics */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-purple)] text-sm uppercase tracking-wider mb-6">./topics_covered</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {details.topics.map((topic, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 bg-[var(--surface)] border border-[var(--border)]">
                <CheckCircle className="w-5 h-5 text-[var(--primary)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-main)]">{topic}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who is this for */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-purple)] text-sm uppercase tracking-wider mb-6">./ideal_audience</p>
          <ul className="space-y-3">
            {details.suitableFor.map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <User className="w-5 h-5 text-[var(--accent-purple)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-main)]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-purple)] text-sm uppercase tracking-wider mb-6">./business_benefits</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {details.benefits.map((benefit, idx) => (
              <div key={idx} className="p-4 bg-[var(--surface)] border border-[var(--border)]">
                <Award className="w-6 h-6 text-[var(--primary)] mb-3" />
                <p className="text-[var(--text-main)]">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-8 md:py-12" data-testid="contact-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="font-mono text-[var(--accent-purple)] text-sm uppercase tracking-wider mb-2">./request_quote</p>
              <h2 className="text-2xl md:text-4xl font-bold text-[var(--text-main)] mb-6">Get a Custom Proposal</h2>
              <p className="text-[var(--text-muted)] text-lg leading-relaxed mb-8">Tell us about your organization and we'll create a tailored training proposal.</p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[var(--accent-purple)]/10 border border-[var(--accent-purple)] flex items-center justify-center flex-shrink-0"><Users className="w-6 h-6 text-[var(--accent-purple)]" /></div>
                  <div><h3 className="font-mono font-bold text-[var(--text-main)] mb-1">Tailored Content</h3><p className="text-[var(--text-muted)] text-sm">Custom curriculum based on your industry.</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[var(--accent-purple)]/10 border border-[var(--accent-purple)] flex items-center justify-center flex-shrink-0"><Shield className="w-6 h-6 text-[var(--accent-purple)]" /></div>
                  <div><h3 className="font-mono font-bold text-[var(--text-main)] mb-1">Measurable Results</h3><p className="text-[var(--text-muted)] text-sm">Track progress and improvements.</p></div>
                </div>
              </div>
            </div>
            <div className="bg-[var(--background)] border border-[var(--border)] p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                {/* Honeypot field for anti-spam */}
                <input type="checkbox" name="botcheck" className="hidden" />

                <div>
                  <label className="block font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-2">Name</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="w-full bg-[var(--surface)] border border-[var(--border)] text-[var(--primary)] font-mono p-3 focus:border-[var(--primary)] focus:outline-none" />
                </div>
                <div>
                  <label className="block font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-2">Email</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="w-full bg-[var(--surface)] border border-[var(--border)] text-[var(--primary)] font-mono p-3 focus:border-[var(--primary)] focus:outline-none" />
                </div>
                <div>
                  <label className="block font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-2">Company</label>
                  <input type="text" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} required className="w-full bg-[var(--surface)] border border-[var(--border)] text-[var(--primary)] font-mono p-3 focus:border-[var(--primary)] focus:outline-none" />
                </div>
                <div>
                  <label className="block font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-2">Message</label>
                  <textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows={4} className="w-full bg-[var(--surface)] border border-[var(--border)] text-[var(--primary)] font-mono p-3 focus:border-[var(--primary)] focus:outline-none resize-none" />
                </div>
                <button type="submit" disabled={isSubmitting} className="w-full bg-[var(--primary)] text-black font-mono font-bold px-8 py-4 uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_15px_var(--glow-primary)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-testid="submit-btn">
                  {isSubmitting ? (
                    <>
                      <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" /> Request Quote
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateDetailPage;
